import type { SceneControl } from '@anov/3d-core'
import { DoubleSide, Object3D, PlaneGeometry, RepeatWrapping, TextureLoader, Water as ThreeWater, Vector3, use } from '@anov/3d-core'
import { WaterImg } from './types'
import type { IWater } from './types'

export interface IWaterOption {
  size?: number
  height?: number
  waterColor?: number
  doubleSided?: boolean
  [key: string]: any
}

const DEFAULT_OPTION = {
  size: 1000,
  height: 0,
  waterColor: 0x001E0F,
  fog: false,
  sunColor: 0xFFFFFF,
  distortionScale: 1.5,
  alpha: 1.0,
  doubleSided: true,
}

class Water extends Object3D implements IWater {
  private _option: IWaterOption
  private _mesh: ThreeWater
  private _disposeCallback: Function | null
  private sceneControl: SceneControl

  constructor(option: IWaterOption, sceneControl: SceneControl) {
    super()
    this.sceneControl = sceneControl
    this._option = { ...DEFAULT_OPTION, ...(option || {}) }
    this.init()
  }

  init() {
    const { size, alpha, height, sunColor, waterColor, distortionScale } = this._option
    const waterGeometry = new PlaneGeometry(size, size)
    const water = new ThreeWater(waterGeometry, {
      textureWidth: 512,
      textureHeight: 512,
      waterNormals: new TextureLoader().load(WaterImg, (texture) => {
        texture.wrapS = texture.wrapT = RepeatWrapping
      }),
      alpha,
      sunDirection: new Vector3(),
      sunColor,
      waterColor,
      distortionScale,
    })
    water.rotation.x = -Math.PI / 2
    water.name = 'SYS_WATER'
    water.material.side = DoubleSide
    water.position.y = height

    this.add(water)
    this._mesh = water
  }

  setSize(size: number) {
    if (!this._mesh) {
      console.warn('Water not created')
      return
    }
    if (this._option.size === size)
      return
    this._option.size = size
    const geometry = new PlaneGeometry(size, size)
    this._mesh.geometry.dispose()
    this._mesh.geometry = geometry
  }

  /**
   * 设置当前高度
   */
  setHeight(height: number): void {
    if (!this._mesh) {
      console.warn('Water not created')
      return
    }
    this._mesh.position.y = height
    this._option.height = height
  }

  /**
   * 获取当前高度
   */
  getHeight(): number {
    return this._mesh?.position.y ?? 0
  }

  update() {
    this._disposeCallback = use.useframeAfter(this.sceneControl, () => {
      if (this._mesh)
        this._mesh.material.uniforms.time.value += 1.0 / 60.0
    })
  }

  destroy() {
    this._disposeCallback && this._disposeCallback()
    this.remove(this._mesh)
  }
}

export { Water }
